import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { portalAuthApi } from '@/lib/api/portal.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function PortalForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [showShopId, setShowShopId] = useState(false);
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      portalAuthApi.forgotPassword({
        email: email.trim(),
        ...(tenantSlug.trim() ? { tenantSlug: tenantSlug.trim() } : {}),
      }),
    onSuccess: () => setDone(true),
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
      if (parsed.code === 'SHOP_ID_REQUIRED') setShowShopId(true);
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-white to-sky-100 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <h1 className="text-2xl font-bold text-[#1B4F8A]">Forgot Password</h1>
        {done ? (
          <p className="mt-4 text-sm text-slate-600">
            If that email exists, we sent a reset link. Check your inbox.
          </p>
        ) : (
          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
          >
            <label className="block text-sm font-medium">
              Email
              <input
                type="email"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            {showShopId ? (
              <label className="block text-sm font-medium">
                Shop ID
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                  placeholder="From your welcome email"
                />
              </label>
            ) : (
              <button
                type="button"
                className="text-xs font-semibold text-slate-500"
                onClick={() => setShowShopId(true)}
              >
                Need a Shop ID?
              </button>
            )}
            <button
              type="submit"
              className="w-full rounded-xl bg-[#1B4F8A] px-4 py-3 text-sm font-semibold text-white"
            >
              Send Reset Link
            </button>
          </form>
        )}
        <Link to="/portal/login" className="mt-4 inline-block text-sm font-semibold text-[#1B4F8A]">
          ← Back to login
        </Link>
      </div>
    </div>
  );
}

export function PortalResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');

  const mutation = useMutation({
    mutationFn: () => portalAuthApi.resetPassword({ token, newPassword: password }),
    onSuccess: () => {
      toastSuccess('Password updated. Please sign in.');
      navigate('/portal/login');
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-100 via-white to-sky-100 px-4">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <h1 className="text-2xl font-bold text-[#1B4F8A]">Reset Password</h1>
        <form
          className="mt-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (password !== confirm) {
              toastError('Passwords do not match');
              return;
            }
            mutation.mutate();
          }}
        >
          <label className="block text-sm font-medium">
            New password
            <input
              type="password"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <label className="block text-sm font-medium">
            Confirm password
            <input
              type="password"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
            />
          </label>
          <button
            type="submit"
            disabled={!token || mutation.isPending}
            className="w-full rounded-xl bg-[#1B4F8A] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
          >
            Update Password
          </button>
        </form>
      </div>
    </div>
  );
}

export function PortalChangePasswordPage() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => portalAuthApi.changePassword({ currentPassword, newPassword }),
    onSuccess: () => {
      toastSuccess('Password changed');
      navigate('/portal/profile');
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    },
  });

  return (
    <div className="mx-auto max-w-md space-y-5">
      <Link to="/portal/profile" className="text-sm font-semibold text-slate-500">
        ← Back
      </Link>
      <h1 className="text-2xl font-bold">Change Password</h1>
      <form
        className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        <label className="block text-sm font-medium">
          Current password
          <input
            type="password"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </label>
        <label className="block text-sm font-medium">
          New password
          <input
            type="password"
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            minLength={8}
            required
          />
        </label>
        <button
          type="submit"
          className="w-full rounded-xl bg-[#1B4F8A] px-4 py-3 text-sm font-semibold text-white"
        >
          Save Password
        </button>
      </form>
    </div>
  );
}
